import { useMemo, useState } from 'react';
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, TouchSensor, closestCorners, useDroppable, useSensor,
  useSensors, type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckSquare, ListChecks, ListTree, MessageSquare, Paperclip, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMoverTarea } from '@/hooks/useTareas';
import type { Tarea, TableroColumna, TableroCompleto } from '@/types/tareas';
import {
  AvatarsAsignados, EtiquetaChip, PrioridadBadge, RelacionChip, VencimientoTexto,
} from '@/components/tareas/TareaPiezas';

interface Props {
  tablero: TableroCompleto;
  onAbrirTarea: (id: string) => void;
  onNuevaTarea: (columnaId: string) => void;
}

const RELACIONES_VISIBLES_EN_TARJETA = 2;

export function KanbanBoard({ tablero, onAbrirTarea, onNuevaTarea }: Props) {
  const mover = useMoverTarea(tablero.tablero.id);
  const [arrastrandoId, setArrastrandoId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const porColumna = useMemo(() => {
    const mapa = new Map<string, Tarea[]>();
    tablero.columnas.forEach(c => mapa.set(c.id, []));
    [...tablero.tareas]
      .sort((a, b) => a.orden - b.orden)
      .forEach(t => mapa.get(t.columnaId)?.push(t));
    return mapa;
  }, [tablero.columnas, tablero.tareas]);

  const arrastrada = arrastrandoId ? tablero.tareas.find(t => t.id === arrastrandoId) ?? null : null;

  const alIniciar = (e: DragStartEvent) => setArrastrandoId(String(e.active.id));

  const alTerminar = (e: DragEndEvent) => {
    setArrastrandoId(null);
    const { active, over } = e;
    if (!over) return;
    const tarea = tablero.tareas.find(t => t.id === active.id);
    if (!tarea) return;

    const datosDestino = over.data.current as { type?: string; columnaId?: string } | undefined;
    let columnaId: string;
    let antesDeId: string | undefined;
    let despuesDeId: string | undefined;

    if (datosDestino?.type === 'column') {
      columnaId = datosDestino.columnaId ?? tarea.columnaId;
    } else {
      const sobre = tablero.tareas.find(t => t.id === over.id);
      if (!sobre) return;
      columnaId = sobre.columnaId;
      if (columnaId === tarea.columnaId) {
        const lista = porColumna.get(columnaId) ?? [];
        const desde = lista.findIndex(t => t.id === tarea.id);
        const hasta = lista.findIndex(t => t.id === sobre.id);
        if (desde === hasta) return;
        const nueva = arrayMove(lista, desde, hasta);
        const previa = nueva[hasta - 1];
        const siguiente = nueva[hasta + 1];
        if (previa) despuesDeId = previa.id;
        else if (siguiente) antesDeId = siguiente.id;
      } else {
        antesDeId = sobre.id;
      }
    }

    mover.mutate({ id: tarea.id, columnaId, antesDeId, despuesDeId });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={alIniciar}
      onDragEnd={alTerminar}
      onDragCancel={() => setArrastrandoId(null)}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 items-start">
        {tablero.columnas.map(columna => (
          <Columna
            key={columna.id}
            columna={columna}
            tareas={porColumna.get(columna.id) ?? []}
            onAbrirTarea={onAbrirTarea}
            onNuevaTarea={() => onNuevaTarea(columna.id)}
          />
        ))}
      </div>
      <DragOverlay>
        {arrastrada ? <TarjetaVisual tarea={arrastrada} flotante /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function Columna({
  columna, tareas, onAbrirTarea, onNuevaTarea,
}: { columna: TableroColumna; tareas: Tarea[]; onAbrirTarea: (id: string) => void; onNuevaTarea: () => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${columna.id}`, data: { type: 'column', columnaId: columna.id } });
  const excedeLimite = columna.limiteWip !== null && tareas.length > columna.limiteWip;

  return (
    <section className="w-72 flex-shrink-0 rounded-xl bg-muted/70 border border-border flex flex-col max-h-[calc(100vh-14rem)]">
      <header className="px-3 pt-3 pb-2 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: columna.color ?? '#94a3b8' }} />
        <h3 className="text-sm font-semibold text-text-primary truncate flex-1">{columna.nombre}</h3>
        {columna.esFinal && <CheckSquare size={13} className="text-emerald-600" aria-label="Columna final" />}
        <span className={cn('text-xs px-1.5 py-0.5 rounded-full', excedeLimite ? 'bg-red-100 text-red-700' : 'bg-white text-text-secondary')}>
          {tareas.length}{columna.limiteWip !== null ? `/${columna.limiteWip}` : ''}
        </span>
        <button
          onClick={onNuevaTarea}
          className="p-1 rounded-md text-text-secondary hover:bg-white hover:text-primary-600"
          aria-label={`Nueva tarea en ${columna.nombre}`}
        >
          <Plus size={15} />
        </button>
      </header>

      <div ref={setNodeRef} className={cn('flex-1 overflow-y-auto px-2 pb-2 space-y-2 min-h-[3rem] rounded-b-xl transition-colors', isOver && 'bg-primary-50/60')}>
        <SortableContext items={tareas.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {tareas.map(t => <TarjetaSortable key={t.id} tarea={t} onAbrir={() => onAbrirTarea(t.id)} />)}
        </SortableContext>
        {!tareas.length && <p className="text-center text-xs text-text-secondary/70 py-4">Sin tareas</p>}
      </div>
    </section>
  );
}

function TarjetaSortable({ tarea, onAbrir }: { tarea: Tarea; onAbrir: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tarea.id,
    data: { type: 'card', columnaId: tarea.columnaId },
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      onClick={onAbrir}
      className={cn('touch-manipulation', isDragging && 'opacity-40')}
    >
      <TarjetaVisual tarea={tarea} />
    </div>
  );
}

function TarjetaVisual({ tarea, flotante = false }: { tarea: Tarea; flotante?: boolean }) {
  const visibles = tarea.relaciones.slice(0, RELACIONES_VISIBLES_EN_TARJETA);
  const restantes = tarea.relaciones.length - visibles.length;
  const completada = !!tarea.completadaAt;

  return (
    <article
      className={cn(
        'rounded-lg bg-white border border-border p-2.5 shadow-sm cursor-grab active:cursor-grabbing hover:border-primary-300 transition-colors',
        flotante && 'shadow-xl rotate-2',
      )}
    >
      {tarea.etiquetas.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {tarea.etiquetas.map(e => <EtiquetaChip key={e.id} etiqueta={e} />)}
        </div>
      )}
      <p className={cn('text-sm font-medium text-text-primary break-words', completada && 'line-through text-text-secondary')}>
        {tarea.titulo}
      </p>

      {tarea.relaciones.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {visibles.map(r => <RelacionChip key={r.id} relacion={r} compacta />)}
          {restantes > 0 && <span className="text-[11px] text-text-secondary self-center">+{restantes}</span>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
        <PrioridadBadge prioridad={tarea.prioridad} />
        <VencimientoTexto tarea={tarea} />
        {tarea.subtareas.total > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-text-secondary" title="Subtareas completadas">
            <ListTree size={11} /> {tarea.subtareas.completadas}/{tarea.subtareas.total}
          </span>
        )}
        {tarea.checklist.total > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-text-secondary" title="Elementos de checklist completados">
            <ListChecks size={11} /> {tarea.checklist.completados}/{tarea.checklist.total}
          </span>
        )}
        {tarea.comentarios > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-text-secondary" title="Comentarios">
            <MessageSquare size={11} /> {tarea.comentarios}
          </span>
        )}
        {tarea.adjuntos > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-text-secondary" title="Adjuntos">
            <Paperclip size={11} /> {tarea.adjuntos}
          </span>
        )}
        <span className="ml-auto"><AvatarsAsignados asignados={tarea.asignados} /></span>
      </div>
    </article>
  );
}
